import { Link, useLocation } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useBottomSheet } from './BottomSheetContext';
import { cn } from '@/lib/utils';

// Floating "?" button bottom-left that links to the Help page, so Help is
// always reachable on every screen for parents who get stuck. (Help also
// lives in the sidebar footer on desktop and the BottomNav "More" sheet on
// mobile — this is the always-visible shortcut, not the only path.)
//
// Hidden on the Help page itself (no point linking to where you already are),
// on chat (chat owns its own bottom-right real estate), and while the
// BottomNav's More overflow sheet is open (FAB sitting on top of a modal
// sheet is visually noisy — M2.11).
//
// `sidebarPinned`: at `md`+ a pinned rail (expanded or collapsed) is always on
// the left and its footer already carries Help; the FAB sat under the rail's
// profile row there, invisible but still a tab stop. `md:hidden` is display
// none, so it also leaves the tab order. In hamburger mode there is no rail at
// md+, so the FAB stays.
export function HelpFAB({ sidebarPinned = false }: { sidebarPinned?: boolean } = {}) {
  const { user } = useAuth();
  const location = useLocation();
  const { isOpen: sheetOpen } = useBottomSheet();
  const { t } = useTranslation('shell');

  if (user?.role !== 'parent') return null;
  if (location.pathname.startsWith('/help')) return null;
  if (location.pathname.startsWith('/chat')) return null;
  if (sheetOpen) return null;

  return (
    <Link
      to="/help"
      aria-label={t('helpFab.label')}
      title={t('helpFab.label')}
      // bottom inset stacks the standard spacing on top of env(safe-area-inset-bottom)
      // so the FAB clears the Android gesture bar / iOS Home indicator on native
      // and the page-bottom edge on web (where safe-area-inset is 0).
      //
      // Mobile bottom value also lifts above the BottomNav (h-16 = 4rem, plus
      // gap) — HelpFAB is parent-only and parent users always see BottomNav on
      // mobile (M2.9). sm: returns to the original above-page-edge position
      // since BottomNav is sm:hidden.
      // hidden on mobile — Help now lives in the BottomNav "More" sheet there;
      // shown sm+ where there's no BottomNav (desktop also has Help in the sidebar).
      className={cn(
        'fixed left-4 z-30 hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-stevens-indigo shadow-sm hover:bg-stevens-indigo/5 hover:border-stevens-indigo/40 hover:text-stevens-indigo-2 transition-colors md:left-6 bottom-[calc(5rem+env(safe-area-inset-bottom))] sm:bottom-[calc(1rem+env(safe-area-inset-bottom))] md:bottom-[calc(1.5rem+env(safe-area-inset-bottom))]',
        sidebarPinned && 'md:hidden',
      )}
    >
      <HelpCircle className="h-5 w-5" />
    </Link>
  );
}
